import { ReactNode, useState } from "react";
import clsx from "clsx";
import { LuHourglass, LuLock, LuPartyPopper, LuWrench, LuX } from "react-icons/lu";
import { GiTurtle } from "react-icons/gi";
import type { Shown, StatusWindow } from "helpers/maintenance/maintenance-state";
import { useServerNow } from "./useMaintenance";

const MIN = 60000;

export function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function fmtDay(ms: number, now: number): string {
  const day = new Date(ms).toDateString();
  if (day === new Date(now).toDateString()) return "today";
  if (day === new Date(now + 1440 * MIN).toDateString()) return "tomorrow";
  return new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short" }).format(ms);
}

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

/** mm:ss, or h:mm:ss from an hour up. */
export function fmtClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = pad2(total % 60);
  return h > 0 ? `${h}:${pad2(m)}:${s}` : `${m}:${s}`;
}

export function fmtLeft(ms: number): string {
  const mins = Math.max(0, Math.ceil(ms / MIN));
  if (mins < 60) return `${mins} min`;
  const m = mins % 60;
  return m === 0 ? `${Math.floor(mins / 60)} h` : `${Math.floor(mins / 60)} h ${m} min`;
}

function fmtIn(ms: number): string {
  if (ms < 60 * MIN) return `in ${Math.max(1, Math.ceil(ms / MIN))} min`;
  const h = Math.round(ms / (60 * MIN));
  return h < 48 ? `in ${h} h` : `in ${Math.round(h / 24)} days`;
}

export function endMs(w: StatusWindow): number | null {
  const end = w.endedAt !== null ? w.endedAt : w.endsAt;
  return end === null ? null : Date.parse(end);
}

/** Share of the window already gone, 0 to 100. */
export function progressPct(w: StatusWindow, now: number): number {
  const start = Date.parse(w.startsAt);
  const end = endMs(w);
  if (end === null || end <= start) return 0;
  return Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
}

const bare = (title: string) => title.replace(/[.!]+$/, "");
const sentence = (title: string) => (/[.!?]$/.test(title) ? title : `${title}.`);

function plannedRange(w: StatusWindow, now: number): string {
  const start = Date.parse(w.startsAt);
  const end = endMs(w);
  const day = fmtDay(start, now);
  return end === null ? `${day}, from ${fmtTime(start)}` : `${day}, ${fmtTime(start)} to ${fmtTime(end)}`;
}

function summary(s: Shown, now: number): string {
  if (s.phase === "notice") {
    const range = plannedRange(s.window, now);
    return range.charAt(0).toUpperCase() + range.slice(1);
  }
  if (s.phase === "soon") return "Starts in a few minutes";
  if (s.phase === "back") return "Finished";
  const end = endMs(s.window);
  return end === null ? "In progress" : `In progress, until about ${fmtTime(end)}`;
}

type Tone = "brand" | "soon" | "slower" | "readOnly" | "back";

function toneOf(s: Shown): Tone {
  if (s.phase === "soon") return "soon";
  if (s.phase === "back") return "back";
  if (s.phase === "live" && s.window.impact === "SLOWER") return "slower";
  if (s.phase === "live" && s.window.impact === "READ_ONLY") return "readOnly";
  return "brand";
}

const ICON = { brand: LuWrench, soon: LuHourglass, slower: GiTurtle, readOnly: LuLock, back: LuPartyPopper };

export function MaintenanceBanner({
  shown,
  onDismiss,
}: {
  shown: Shown[];
  onDismiss: (w: StatusWindow) => void;
}) {
  const [open, setOpen] = useState(false);
  const top = shown[0];
  const w = top.window;
  const phase = top.phase;
  const tone = toneOf(top);
  const end = endMs(w);
  const counting = phase === "notice" || phase === "soon" || (tone === "readOnly" && end !== null);
  const now = useServerNow(counting);
  const Icon = ICON[tone];
  const amber = tone === "slower";
  const pill = amber ? "mw-pill-amber" : "mw-pill";
  const dismissible =
    phase === "notice" || phase === "back" || (phase === "live" && (w.impact === "HEADS_UP" || w.impact === "SLOWER"));
  const others = shown.slice(1);
  const update = phase === "live" && w.latestUpdate ? w.latestUpdate : null;

  let lead: string;
  let body: ReactNode = w.message;
  if (phase === "notice") lead = `${bare(w.title)}: ${plannedRange(w, now)}.`;
  else if (phase === "soon") {
    lead = `${bare(w.title)} starts in a few minutes.`;
    body = <span className="mw-muted">Please save your work.</span>;
  } else if (phase === "back") {
    lead = "We're back.";
    body = "Everything is up and running again. Thanks for your patience.";
  } else if (tone === "readOnly") {
    lead = end === null ? "Saving is paused for now." : `Saving is paused until about ${fmtTime(end)}.`;
    body = w.message || "You can still view everything.";
  } else lead = sentence(w.title);

  return (
    <div
      role={phase === "soon" || phase === "live" ? "alert" : "status"}
      className={`mw-strip mw-strip-${tone} position-relative`}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(false);
      }}
    >
      <div className="mw-row d-flex align-items-center gap-3">
        <span className="mw-tile flex-shrink-0" aria-hidden="true">
          <Icon />
        </span>
        <p className="flex-grow-1 mb-0 mw-min0">
          <b>{lead}</b> {body}
          {update && (
            <span className={clsx("d-block mt-1", amber ? "d-sm-none mw-update-amber" : "mw-update")}>
              Latest, {fmtTime(Date.parse(update.at))}: {update.message}
            </span>
          )}
        </p>
        {update && amber && (
          <span className={clsx(pill, "d-none d-sm-inline-flex align-items-center gap-2 flex-shrink-0")}>
            <b>{fmtTime(Date.parse(update.at))}</b> {update.message}
          </span>
        )}
        {phase === "notice" && <span className={clsx(pill, "flex-shrink-0")}>{fmtIn(Date.parse(w.startsAt) - now)}</span>}
        {phase === "soon" && (
          <>
            <span className="mw-clock flex-shrink-0" aria-hidden="true">
              {fmtClock(Date.parse(w.startsAt) - now)}
            </span>
            <span className="visually-hidden">Starts in {fmtLeft(Date.parse(w.startsAt) - now)}</span>
          </>
        )}
        {tone === "readOnly" && end !== null && (
          <>
            <div className="mw-bar d-none d-sm-block flex-shrink-0" aria-hidden="true">
              <div style={{ width: `${progressPct(w, now)}%` }} />
            </div>
            <span className={clsx(pill, "flex-shrink-0")}>{fmtLeft(end - now)} left</span>
          </>
        )}
        {others.length > 0 && (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className={clsx(pill, "mw-more flex-shrink-0")}
          >
            +{others.length} more
          </button>
        )}
        {dismissible && (
          <button type="button" aria-label="Dismiss" onClick={() => onDismiss(w)} className="mw-close flex-shrink-0">
            <LuX aria-hidden="true" />
          </button>
        )}
      </div>
      {tone === "soon" && <div className="mw-hazard" aria-hidden="true" />}
      {open && others.length > 0 && (
        <ul className="mw-list list-unstyled mb-0">
          {others.map((s) => (
            <li key={s.window.id}>
              <p className="mb-0 fw-bold">{bare(s.window.title)}</p>
              <p className="mb-0 mw-list-sub">{summary(s, now)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
